import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react'
import { ArrowUp, Square } from 'lucide-react'
import { Field, IconButton } from '../../ui'

/** The textarea grows from one line to this many, then scrolls. */
const MAX_LINES = 4

/**
 * Where Doug types. Fixed above the tab bar; the thread scrolls under it.
 *
 * `onHeight` reports the composer's own height, measured by a
 * `ResizeObserver` rather than guessed (WP 6.9's lesson), so the column can
 * pad its foot by exactly that much and the last message is never under the
 * composer. The observer is attached in this component's own layout effect,
 * and this component is never behind a pending query — so it runs on the
 * first commit that has the element, cold load included (CLAUDE.md's
 * warm-render trap: a `[]` effect in a parent that first renders
 * `<Loading/>` never sees the ref). Hidden under X7 it measures 0, which
 * pads a hidden pane by nothing; shown again, the observer fires with the
 * real height.
 *
 * Enter sends, Shift+Enter breaks a line, and neither fires mid-composition
 * (a Japanese IME's Enter confirms a word, it does not send). During a turn
 * the send button is swapped for Stop, which aborts the stream.
 *
 * The same `ResizeObserver` also writes `--chat-composer-h` onto `<html>`
 * and toggles a `has-chat-composer` class there (WP 9.8) — not only the
 * local custom property `Thread.tsx` reads for its own padding — so
 * `ui.css`'s toast host can raise itself above the composer while this
 * tab is the one on screen. The class comes off whenever the measured
 * height is 0, which X7 already makes true the moment this pane is
 * hidden (see the note above), so no other tab's toast moves.
 */
export function Composer({ streaming, onSend, onStop, onHeight, placeholder = 'Ask about an evening…', seed = null }: {
  streaming: boolean
  onSend: (text: string) => void
  onStop: () => void
  onHeight: (px: number) => void
  /** WP 9.12 — "Or type an answer…" while the butler's question allows one. */
  placeholder?: string
  /** WP 9.12 — Edit puts Doug's last message back here. A new object each
   *  time, so editing the same words twice still lands. */
  seed?: { text: string } | null
}) {
  const [text, setText] = useState('')
  const box = useRef<HTMLDivElement>(null)
  const input = useRef<HTMLTextAreaElement>(null)
  const heightRef = useRef(onHeight)
  heightRef.current = onHeight

  useEffect(() => {
    if (!seed) return
    setText(seed.text)
    const el = input.current
    if (el) {
      el.focus({ preventScroll: true })
      // The caret after the last word, where an edit usually starts. A frame
      // late: the value is not in the textarea until React has committed it.
      requestAnimationFrame(() => el.setSelectionRange(el.value.length, el.value.length))
    }
  }, [seed])

  useLayoutEffect(() => {
    const el = box.current
    if (!el) return
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) {
        const h = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height
        const px = Math.ceil(h)
        heightRef.current(px)
        document.documentElement.style.setProperty('--chat-composer-h', `${px}px`)
        document.documentElement.classList.toggle('has-chat-composer', px > 0)
      }
    })
    ro.observe(el)
    return () => {
      ro.disconnect()
      document.documentElement.classList.remove('has-chat-composer')
    }
  }, [])

  // One line to four: measure the content, cap it, scroll past the cap.
  useLayoutEffect(() => {
    const el = input.current
    if (!el) return
    const cs = getComputedStyle(el)
    const line = parseFloat(cs.lineHeight) || 24
    const padY = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom)
    const border = parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth)
    const max = line * MAX_LINES + padY + border
    el.style.height = 'auto'
    const want = el.scrollHeight + border
    el.style.height = `${Math.min(want, max)}px`
    el.style.overflowY = want > max ? 'auto' : 'hidden'
  }, [text])

  const canSend = !streaming && text.trim().length > 0

  function send() {
    if (!canSend) return
    onSend(text.trim())
    setText('')
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return
    e.preventDefault()
    send()
  }

  return (
    <div className="chat-composer" ref={box}>
      <div className="chat-composer__inner">
        <Field.Textarea
          ref={input}
          className="chat-composer__input"
          rows={1}
          aria-label="Ask"
          placeholder={placeholder}
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={onKeyDown}
          enterKeyHint="send"
        />
        {streaming ? (
          <IconButton label="Stop" className="chat-composer__btn" onClick={onStop}>
            <Square aria-hidden="true" />
          </IconButton>
        ) : (
          <IconButton label="Send" className="chat-composer__btn chat-composer__btn--send"
            onClick={send} disabled={!canSend}>
            <ArrowUp aria-hidden="true" />
          </IconButton>
        )}
      </div>
    </div>
  )
}
